/**
 * Demo — book-a-demo form per product-copy spec §4.11.
 *
 * The form posts to a placeholder no-op for now; production wiring is on the
 * Tier-2 backlog (route to a sales email or CRM webhook). Form fields per spec.
 */
import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import MarketingNav from "../components/MarketingNav";
import MarketingFooter from "../components/MarketingFooter";
import MetaTags from "../components/MetaTags";
import ContactPanel from "../components/ContactPanel";
import { useI18n } from "../lib/i18n";

const CHILD_RANGES = [
  "Fewer than 25 children",
  "25 – 75",
  "75 – 200",
  "200 – 500",
  "More than 500",
];

export default function Demo() {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: "",
    email: "",
    center: "",
    range: CHILD_RANGES[1],
    country: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  function update(field) {
    return (e) => setForm({ ...form, [field]: e.target.value });
  }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/demo-request", {
        name: form.name,
        email: form.email,
        center: form.center,
        range: form.range,
        country: form.country,
        notes: form.notes || null,
      });
      setSubmitted(true);
      toast.success(t("marketing.demo.thanks_body"));
    } catch (err) {
      // FastAPI returns 422 detail as an array; everything else as a string.
      // Coerce to a single calm sentence so React + toast never receive an array.
      const raw = err?.response?.data?.detail;
      let msg = t("errors.generic");
      if (typeof raw === "string" && raw) msg = raw;
      else if (Array.isArray(raw) && raw.length > 0)
        msg = (raw[0]?.msg || msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-white" data-testid="demo-screen">
      <MetaTags
        title={t("marketing.demo.meta_title")}
        description={t("marketing.demo.meta_description")}
      />
      <MarketingNav />
      <main className="pt-32 sm:pt-40 pb-24">
        <section
          aria-labelledby="contact-panel-heading"
          className="mx-auto max-w-7xl px-6 sm:px-10 mb-12"
        >
          <ContactPanel />
        </section>

        <section
          aria-labelledby="demo-headline"
          className="mx-auto max-w-7xl px-6 sm:px-10"
        >
          <div className="rounded-3xl bg-[#1F3A2A] text-white px-8 sm:px-14 py-14 sm:py-20 shadow-[0_16px_40px_rgba(107,91,231,0.28)]">
            <div className="max-w-3xl mx-auto text-center">
              <span
                data-testid="demo-eyebrow"
                className="text-xs tracking-[0.04em] uppercase font-semibold text-[#E6EFE8]"
              >
                {t("marketing.demo.eyebrow")}
              </span>
              <h1
                id="demo-headline"
                data-testid="demo-headline"
                className="mt-4 font-display text-4xl sm:text-6xl tracking-tight leading-[1.05]"
              >
                {t("marketing.demo.headline_lead")}{" "}
                <em className="italic">
                  {t("marketing.demo.headline_italic")}
                </em>{" "}
                {t("marketing.demo.headline_tail")}
              </h1>
              <p className="mt-6 text-lg text-white/85 leading-relaxed max-w-xl mx-auto">
                {t("marketing.demo.subhead")}
              </p>
            </div>

            {!submitted ? (
              <form
                onSubmit={submit}
                data-testid="demo-form"
                className="mt-12 max-w-xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white rounded-2xl p-7 text-[#1A1B2E]"
              >
                <Field label={t("marketing.demo.field_name")} required>
                  <input
                    data-testid="demo-name"
                    required
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    className="input-soft w-full"
                  />
                </Field>
                <Field label={t("marketing.demo.field_email")} required>
                  <input
                    data-testid="demo-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="input-soft w-full"
                  />
                </Field>
                <Field label={t("marketing.demo.field_center")} required>
                  <input
                    data-testid="demo-center"
                    required
                    type="text"
                    value={form.center}
                    onChange={update("center")}
                    className="input-soft w-full"
                  />
                </Field>
                <Field label={t("marketing.demo.field_range")}>
                  <select
                    data-testid="demo-range"
                    value={form.range}
                    onChange={update("range")}
                    className="input-soft w-full"
                  >
                    {CHILD_RANGES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t("marketing.demo.field_country")} required>
                  <input
                    data-testid="demo-country"
                    required
                    type="text"
                    value={form.country}
                    onChange={update("country")}
                    className="input-soft w-full"
                  />
                </Field>
                <Field label={t("marketing.demo.field_notes")} full>
                  <textarea
                    data-testid="demo-notes"
                    value={form.notes}
                    onChange={update("notes")}
                    rows={3}
                    className="input-soft w-full"
                  />
                </Field>
                <div className="sm:col-span-2 flex flex-col items-center gap-3 mt-2">
                  <button
                    type="submit"
                    data-testid="demo-submit"
                    disabled={submitting}
                    className="btn-primary"
                  >
                    {submitting
                      ? t("marketing.demo.submit_pending")
                      : t("marketing.demo.submit")}
                  </button>
                  <p className="text-xs text-[#6B7280] text-center max-w-md">
                    {t("marketing.demo.consent_prefix")}{" "}
                    <a
                      href="/privacy"
                      className="text-[#1F3A2A] underline underline-offset-4"
                    >
                      {t("marketing.demo.consent_privacy_link")}
                    </a>
                    .
                  </p>
                </div>
              </form>
            ) : (
              <div
                data-testid="demo-success"
                className="mt-12 max-w-xl mx-auto bg-white rounded-2xl p-10 text-[#1A1B2E] text-center"
              >
                <div className="font-display text-3xl tracking-tight" data-testid="demo-thanks-headline">
                  {t("marketing.demo.thanks_headline_personal", {
                    name: form.name.split(" ")[0] || t("marketing.demo.thanks_default_name"),
                  })}
                </div>
                <p className="mt-4 text-[#6B7280]">
                  {t("marketing.demo.thanks_body")}
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}

function Field({ label, children, required, full }) {
  return (
    <label className={"block " + (full ? "sm:col-span-2" : "")}>
      <span className="block text-xs uppercase tracking-[0.04em] text-[#6B7280] font-semibold mb-1.5">
        {label}
        {required && <span className="text-[#FF6B6B] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
